import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Table } from 'antd';
import tw from 'twin.macro';
import { colorConstants } from 'config/colorConstants';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import CopyScenario from 'components/organism/PriceSimulator/Modals/CopyScenario';
import ConfirmEventModal from 'components/organism/PromoRecommendation/ConfirmationModal';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchScenarioNames } from 'hooks/priceSimulator';
import { useCopyScenarioEvent } from 'hooks/priceSimulator/useCopyScenarioEvent';
import { useDeleteScenarioEvent } from 'hooks/priceSimulator/useDeleteEvent';
import { getAllScenarioListColumns } from 'utils/getPriceSimulatorColumns';
import { extractEmailAndName, getConfig, sendNotification } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_SELECTION_CHECKBOX_CLICKED,
    PSR_COMPARE_SCENARIO_BUTTON_CLICKED,
    PSR_COPY_EVENT_CLICKED,
    PSR_CREATE_SCENARIO_BUTTON_CLICKED,
    PSR_DELETE_EVENT_CLICKED,
} from 'utils/mixpanelEventNames';

export const commonClassName = 'border  border-borderLinePurpuleAlt p-4 rounded-lg';
export const commonHeaderClass = 'font-medium font-xl m-0 text-headerColor';

type SimulationListType = {
    setSimulationTile: Function;
    simulationTileData: Array<any>;
    selectedRowKeys: React.Key[];
    setSelectedRowKeys: Function;
    allScenariosDataSet: any;
    setAllScenariosDataSet: Function;
    allScenariosLoading: boolean;
};

const SimulationList = ({
    setSimulationTile,
    simulationTileData,
    selectedRowKeys,
    setSelectedRowKeys,
    allScenariosDataSet,
    setAllScenariosDataSet,
    allScenariosLoading,
}: SimulationListType) => {
    const { filterNullError, allDimAndAttributeTypeList, idForRestOfComponentLoading } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const { dataSources } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);
    const simulatorPageRef: any = useRef(null);
    const [showDeletePopUp, setShowDeletePopUp] = useState(false);
    const [showCopyPopUp, setShowCopyPopUp] = useState(false);
    const [copyScenarioName, setCopyScenarioName] = useState('');

    const deleteScenarioId = useRef<any>(null);
    const copyScenarioId = useRef<any>(null);

    const [allScenarioTableHeight, setAllScenarioTableHeight] = useState(400);

    // scenario names GQL
    const {
        loading: scenarioNamseLoading,
        data: scenarioNamesData,
        error: scenarioNamesError,
        fetchScenarioNames,
    } = useFetchScenarioNames();

    // fetch scenario names
    useEffect(() => {
        fetchScenarioNames();
    }, []);

    const {
        loading: deleteEventLoading,
        error: deleteEventError,
        deleteScenarioEvent,
    } = useDeleteScenarioEvent({ onComplete: onDeleteQueryComplete });

    const {
        loading: copyEventLoading,
        error: copyEventError,
        copyScenarioEvent,
    } = useCopyScenarioEvent({ onComplete: onCopyQueryComplete });

    useEffect(() => {
        if (simulatorPageRef?.current) {
            const rect = simulatorPageRef?.current.getBoundingClientRect();
            const tableHeight = (rect?.height ?? 700) - 200;
            setAllScenarioTableHeight(tableHeight);
        }
    }, [simulatorPageRef.current]);

    const onDeleteClick = (key: string) => {
        deleteScenarioId.current = key;
        setShowDeletePopUp(true);
        trackEventMixpanel(PSR_DELETE_EVENT_CLICKED, {
            scenariId: key,
        });
    };

    const onConfirmDeleteHandler = () => {
        deleteScenarioEvent({ variables: { scenarioId: deleteScenarioId.current } });
        setShowDeletePopUp(false);
    };

    //called when deleteEvent query is completed
    function onDeleteQueryComplete() {
        const updatedDataSet = allScenariosDataSet.filter((item: any) => item.scenarioId !== deleteScenarioId.current);
        setAllScenariosDataSet(updatedDataSet);
    }

    // step 1
    const onCopyClick = (key: string) => {
        copyScenarioId.current = key;
        setCopyScenarioName(
            allScenariosDataSet?.find((item: any) => item.scenarioId === copyScenarioId.current).Scenario + '_Copy',
        );
        setShowCopyPopUp(true);
        trackEventMixpanel(PSR_COPY_EVENT_CLICKED, {
            scenariId: key,
        });
    };

    // step 2
    const onCopyConfrimClick = () => {
        const userId = extractEmailAndName();
        copyScenarioEvent({
            variables: { scenarioId: copyScenarioId.current, scenarioName: copyScenarioName, userId: userId },
        });
        setShowCopyPopUp(false);
    };

    // step 3 called when copy query is completed
    function onCopyQueryComplete(newScenarioId: number) {
        const newCopiedRow = {
            ...allScenariosDataSet.filter((item: any) => item.scenarioId === copyScenarioId.current)[0],
        };
        // update created by to the new user
        newCopiedRow['User Name'] = extractEmailAndName();
        newCopiedRow.Scenario = copyScenarioName;
        newCopiedRow.scenarioId = newScenarioId;
        newCopiedRow.key = newScenarioId;
        setAllScenariosDataSet([newCopiedRow, ...allScenariosDataSet]);
    }

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        trackEventMixpanel(PSR_SELECTION_CHECKBOX_CLICKED, {
            selectionRows: newSelectedRowKeys,
        });
        const uniqueSources: any = {};
        allScenariosDataSet
            .filter((item: any) => newSelectedRowKeys.includes(item.key))
            .forEach((i: any) => (uniqueSources[i['Data Source']] = 1));

        if (Object.keys(uniqueSources).length > 1) {
            sendNotification('error', 'Please select Scenario from Data Source');
            return;
        }
        if (newSelectedRowKeys.length > 5) {
            sendNotification('error', 'You can select upto 5 plans');
            return;
        }
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        hideSelectAll: true,
    };

    const scenarioListingMemoisedColumns = useMemo(() => {
        return getAllScenarioListColumns(
            allScenariosDataSet,
            onCopyClick,
            onDeleteClick,
            ['scenarioId', 'key'],
            dataSources,
            extractEmailAndName(),
        );
    }, [allScenariosDataSet]);

    const commonLoading =
        allDimAndAttributeTypeList?.loading ||
        allScenariosLoading ||
        idForRestOfComponentLoading ||
        deleteEventLoading ||
        copyEventLoading;

    return (
        <div style={{ minHeight: 'calc(100vh - 100px)' }} id="price-sim-list" ref={simulatorPageRef}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div className="flex flex-col h-full">
                    <div className="flex mb-4 mt-6">
                        <div className="flex-grow font-medium text-subHeaderColor font-lg ">Recent Scenarios</div>
                        <div className=" flex justify-between mr-2">
                            <button
                                onClick={() => {
                                    setSimulationTile(simulationTileData[1]);
                                    trackEventMixpanel(PSR_COMPARE_SCENARIO_BUTTON_CLICKED, {
                                        selectedRowKeys: selectedRowKeys,
                                    });
                                }}
                                className={`rounded cursor-pointer border    font-semibold py-1 px-4  mr-2 ${
                                    selectedRowKeys.length === 0
                                        ? ' pointer-events-none text-borderGrey border-borderGrey'
                                        : 'text-purpleFont border-purpleFont'
                                }`}
                            >
                                Compare Scenarios
                            </button>

                            <Link
                                to="/pricing/recommendation/price-simulator/new"
                                className="rounded cursor-pointer bg-purpleFont hover:text-white text-white font-semibold py-1 px-4 "
                                onClick={() =>
                                    trackEventMixpanel(PSR_CREATE_SCENARIO_BUTTON_CLICKED, {
                                        selectedRowKeys: selectedRowKeys,
                                    })
                                }
                            >
                                Create Scenarios
                            </Link>
                        </div>
                    </div>
                    {/* load scenarios  */}
                    {allScenariosDataSet?.length || commonLoading ? (
                        <div className="rounded overflow-hidden " id="price-sim-list-table">
                            <CustomTablePriceSimulationInput
                                bordered
                                style={{
                                    opacity: commonLoading ? '0.5' : 1,
                                    pointerEvents: commonLoading ? 'none' : 'auto',
                                }}
                                columns={scenarioListingMemoisedColumns}
                                pagination={false}
                                scroll={{ x: '100%', y: allScenarioTableHeight }}
                                className="mb-24"
                                sticky
                                loading={commonLoading}
                                dataSource={allScenariosDataSet}
                                rowSelection={rowSelection}
                            />
                        </div>
                    ) : (
                        // empty screen
                        <div
                            className="h-full flex flex-col border border-borderLinePurpuleAlt"
                            style={{ minHeight: 'calc(100vh - 226px)' }}
                        >
                            <div className="h-12 bg-bgBackground"></div>
                            <div className="flex flex-grow items-center justify-center border-t border-borderLinePurpuleAlt">
                                <span className="font-normal text-fontGreyAlt1 font-md">
                                    Create new scenario from the button above
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <CopyScenario
                visible={showCopyPopUp}
                closeModal={function (): void {
                    setShowCopyPopUp(false);
                }}
                onCopyConfrimClick={onCopyConfrimClick}
                copyScenarioName={copyScenarioName}
                setCopyScenarioName={setCopyScenarioName}
                isDuplicateName={scenarioNamesData?.includes(copyScenarioName) || false}
            />
            <ConfirmEventModal
                onYes={onConfirmDeleteHandler}
                setShowModal={setShowDeletePopUp}
                showModal={showDeletePopUp}
                confirmationTextFromParent={`${
                    allScenariosDataSet?.find((item: any) => item.scenarioId === deleteScenarioId.current)?.Scenario
                } will be Deleted`}
                minWidth="300px"
            />
        </div>
    );
};

export default SimulationList;

export const CustomTablePriceSimulationInput = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }

    border: ${'1px solid #DBDFE9'};
    .ant-checkbox-checked .ant-checkbox-inner {
        ${tw`bg-purpleFont border-purpleFont`}
    }

    .ant-table-row-selected,
    .ant-table-row-selected:hover {
        background-color: none !important;
    }

    .ant-table-filter-trigger {
        margin: 0px;

        &.active {
            color: ${colorConstants.purpleFont} !important;
        }
    }

    .ant-table-tbody > tr.ant-table-row-selected > td {
        background-color: white;
    }
`;
